import { useEffect, useState } from "react";
import { Link2, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const entityTypes = ["exploration", "knowledge", "idea", "question"];
const relationships = [
  "relates_to",
  "inspires",
  "applies_to",
  "contrasts_with",
  "extends",
  "answers",
];

const blankConnection = {
  from_type: "exploration",
  from_id: "",
  to_type: "knowledge",
  to_id: "",
  relationship_type: "relates_to",
  note: "",
};

export const OwnerLabConnectionDialog = ({
  connection,
  entities,
  onDelete,
  onOpenChange,
  onSave,
  open,
}) => {
  const [form, setForm] = useState(blankConnection);
  const [saving, setSaving] = useState(false);
  const isNew = !connection?.id;

  useEffect(() => {
    setForm({ ...blankConnection, ...(connection || {}) });
  }, [connection, open]);

  const displayOptions = {
    from: entities[form.from_type] || [],
    to: entities[form.to_type] || [],
  };

  const setValue = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const changeType = (side, value) => {
    setForm((current) => ({
      ...current,
      [`${side}_type`]: value,
      [`${side}_id`]: "",
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onSave(connection?.id, form);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!connection?.id || !window.confirm("Delete this connection?")) return;
    setSaving(true);
    try {
      await onDelete(connection.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="owner-lab-dialog" data-testid="owner-lab-connection-dialog">
        <DialogHeader>
          <p className="owner-lab-dialog-kicker">Manual connection</p>
          <DialogTitle>{isNew ? "Connect two threads" : "Edit connection"}</DialogTitle>
          <DialogDescription>
            Explain a relationship in your own words. AI suggestions can build on these links later.
          </DialogDescription>
        </DialogHeader>
        <form data-testid="owner-lab-connection-form" onSubmit={save}>
          <div className="owner-lab-connection-grid">
            <label htmlFor="owner-lab-connection-from-type">
              From type
              <select
                data-testid="owner-lab-connection-from-type"
                id="owner-lab-connection-from-type"
                onChange={(event) => changeType("from", event.target.value)}
                value={form.from_type}
              >
                {entityTypes.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <label htmlFor="owner-lab-connection-from-id">
              From item
              <select
                data-testid="owner-lab-connection-from-id"
                id="owner-lab-connection-from-id"
                onChange={(event) => setValue("from_id", event.target.value)}
                required
                value={form.from_id}
              >
                <option value="">Choose an item</option>
                {displayOptions.from.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
              </select>
            </label>
            <label htmlFor="owner-lab-connection-to-type">
              To type
              <select
                data-testid="owner-lab-connection-to-type"
                id="owner-lab-connection-to-type"
                onChange={(event) => changeType("to", event.target.value)}
                value={form.to_type}
              >
                {entityTypes.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <label htmlFor="owner-lab-connection-to-id">
              To item
              <select
                data-testid="owner-lab-connection-to-id"
                id="owner-lab-connection-to-id"
                onChange={(event) => setValue("to_id", event.target.value)}
                required
                value={form.to_id}
              >
                <option value="">Choose an item</option>
                {displayOptions.to.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
              </select>
            </label>
            <label htmlFor="owner-lab-connection-relationship">
              Relationship
              <select
                data-testid="owner-lab-connection-relationship"
                id="owner-lab-connection-relationship"
                onChange={(event) => setValue("relationship_type", event.target.value)}
                value={form.relationship_type}
              >
                {relationships.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
              </select>
            </label>
            <label className="owner-lab-editor-wide" htmlFor="owner-lab-connection-note">
              Why does this connection matter?
              <textarea
                data-testid="owner-lab-connection-note"
                id="owner-lab-connection-note"
                onChange={(event) => setValue("note", event.target.value)}
                value={form.note}
              />
            </label>
          </div>
          <DialogFooter>
            {!isNew && (
              <Button data-testid="owner-lab-connection-delete" disabled={saving} onClick={remove} type="button" variant="outline">
                <Trash2 size={15} />
                Delete
              </Button>
            )}
            <Button data-testid="owner-lab-connection-save" disabled={saving} type="submit">
              <Link2 size={15} />
              <Save size={15} />
              {saving ? "Saving…" : isNew ? "Create connection" : "Save connection"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};